import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "../../shared/ui/PageHeader";
import { apiError } from "../../shared/api/client";
import {
  platformApi,
  platformQueryKeys,
  type PlatformTenant,
} from "../../shared/api/platform";
import { PlatformResultsLookupDialog } from "./PlatformResultsLookupDialog";
import { PlatformTenantScreensDialog } from "./PlatformTenantScreensDialog";

/** SaaS platform console — list & manage clinics (tenants). ADR-0021. */
export function PlatformTenantsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: platformQueryKeys.tenants(),
    queryFn: () => platformApi.listTenants(),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, suspend }: { id: string; suspend: boolean }) =>
      suspend ? platformApi.suspendTenant(id) : platformApi.activateTenant(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: platformQueryKeys.tenants() }),
  });

  const tenants = q.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Phòng khám (Tenant)"
        description="Quản lý toàn bộ phòng khám trên nền tảng Addy HIS."
        actions={
          <Button
            type="button"
            onClick={() => navigate("/platform/tenants/new")}
          >
            <Plus />
            Tạo phòng khám
          </Button>
        }
      />

      {q.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}
      {q.isError && (
        <p className="text-sm text-destructive">{apiError(q.error).detail}</p>
      )}
      {setStatus.isError && (
        <p className="text-sm text-destructive">
          {apiError(setStatus.error).detail}
        </p>
      )}

      {q.data && (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tên phòng khám</TableHead>
                <TableHead>Mã</TableHead>
                <TableHead>Trạng thái</TableHead>
                <TableHead>Ngày tạo</TableHead>
                <TableHead className="text-right">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground"
                  >
                    Chưa có phòng khám nào. Nhấn “Tạo phòng khám” để bắt đầu.
                  </TableCell>
                </TableRow>
              )}
              {tenants.map((t) => (
                <TenantRow
                  key={t.id}
                  t={t}
                  busy={setStatus.isPending}
                  onSuspend={() =>
                    setStatus.mutate({ id: t.id, suspend: true })
                  }
                  onActivate={() =>
                    setStatus.mutate({ id: t.id, suspend: false })
                  }
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function TenantRow({
  t,
  busy,
  onSuspend,
  onActivate,
}: {
  t: PlatformTenant;
  busy: boolean;
  onSuspend: () => void;
  onActivate: () => void;
}) {
  const active = t.status === "ACTIVE";
  const [lookupOpen, setLookupOpen] = useState(false);
  const [screensOpen, setScreensOpen] = useState(false);
  return (
    <TableRow>
      <TableCell className="font-medium">{t.name}</TableCell>
      <TableCell>
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
          {t.code}
        </code>
      </TableCell>
      <TableCell>
        <Badge variant={active ? "success" : "destructive"}>
          {active
            ? "Đang hoạt động"
            : t.status === "SUSPENDED"
              ? "Tạm ngưng"
              : t.status}
        </Badge>
      </TableCell>
      <TableCell>{new Date(t.createdAt).toLocaleDateString("vi-VN")}</TableCell>
      <TableCell className="text-right">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setScreensOpen(true)}
        >
          Gói &amp; tính năng
        </Button>
        <PlatformTenantScreensDialog
          tenant={t}
          open={screensOpen}
          onOpenChange={setScreensOpen}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setLookupOpen(true)}
        >
          Tra cứu kết quả
        </Button>
        <PlatformResultsLookupDialog
          tenant={t}
          open={lookupOpen}
          onOpenChange={setLookupOpen}
        />
        {active ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onSuspend}
          >
            Tạm ngưng
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onActivate}
          >
            Kích hoạt
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
